<script lang="ts" setup>
    import { type JourneyNode } from '@journeys/Journeys.viewmodel';
    import { onMounted, ref, watch, nextTick, computed } from 'vue';
    import ListItemSentStats from '@cwd/views/Issues/ListItemSentStats.vue';
    import TextMessageSentStats from '@cwd/views/TextMessages/List/Components/TextMessageSentStats.vue';
    import vm from '@journeys/Journeys.viewmodel';

    const props = defineProps<{
        node: JourneyNode;
        showStatsOverlay: boolean;
    }>();

    const { nav } = vm;

    const nodeOverlayContainer = ref<HTMLElement | null>(null);
    const movingX = ref(false);
    const movingY = ref(false);
    const nodeId = `jn${props.node.id}`;
    const hasCalculatedPosition = ref(false);

    const updateOverlayPosition = () => {
        hasCalculatedPosition.value = false;
        nav.z = 0; // reset zoom to 0 to fix position calculation

        if (!nodeOverlayContainer.value) return;

        const containerElement = document.querySelector('.canvas') as HTMLElement;
        const nodeElement = document.querySelector(`#${nodeId}`) as HTMLElement;

        if (!nodeElement || !containerElement) return;

        setTimeout(() => {
            const nodeRect = nodeElement.getBoundingClientRect();
            const canvasRect = containerElement.getBoundingClientRect();

            const offsetX = nodeRect.width; //  - 10;
            const offsetY = nodeRect.height; // * -1 - 15;

            nodeOverlayContainer.value!.style.left = `${nodeRect.left - canvasRect.left + offsetX}px`;
            nodeOverlayContainer.value!.style.top = `${nodeRect.top - canvasRect.top + offsetY}px`;

            hasCalculatedPosition.value = true;
        }, zoomTransitionDelayMs);
    };

    const timeoutMs = 500;
    const zoomTransitionDelayMs = 300;

    onMounted(() => {
        watch(
            () => props.node.x,
            () => {
                movingX.value = true;
                nextTick(() => {
                    setTimeout(() => {
                        movingX.value = false;
                    }, timeoutMs);
                });
            }
        );

        watch(
            () => props.node.y,
            () => {
                movingY.value = true;
                nextTick(() => {
                    setTimeout(() => {
                        movingY.value = false;
                    }, timeoutMs);
                });
            }
        );
    });

    const showOverlay = computed(() => {
        return props.showStatsOverlay && !movingX.value && !movingY.value;
    });

    watch(showOverlay, () => {
        if (!showOverlay.value || movingX.value || movingY.value) return;
        updateOverlayPosition();
    });
</script>

<template>
    <div ref="nodeOverlayContainer" class="node-overlay-container" :class="{ in: showOverlay && hasCalculatedPosition }" :id="`node-overlay-${nodeId}`">
        <ListItemSentStats
            v-if="node.data.IssueId"
            :issueStatistics="node.data.issueStats || null"
            :showReceivedAsCount="true"
            :showExactCounts="true"
            :journeyNodeOverlay="true"
            :loading="node.data.loadingStats" />

        <TextMessageSentStats
            v-if="node.data.SmsMessageId"
            :textMessage="node.data.textMessageStats || null"
            :showReceivedAsCount="true"
            :showExactCounts="true"
            :journeyNodeOverlay="true"
            :loading="node.data.loadingStats" />
    </div>
</template>

<style>
    .node-overlay-container {
        position: absolute;
        opacity: 0;
        transform: scale(0);
        transition:
            left 0.2s ease-in-out,
            top 0.2s ease-in-out,
            opacity 0.2s ease-in-out,
            transform 0.2s ease-in-out;
        z-index: 99;
        pointer-events: none;
        margin-top: calc(70px * -1 - 15px);
        margin-left: -10px;
    }

    .node-overlay-container.in {
        opacity: 1;
        transform: scale(1);
    }

    .node-overlay {
        --node-overlay-width: 160px;
        background-color: #fff;
        border-radius: var(--border-radius);
        padding: var(--ug-spacing-xs) var(--ug-spacing-sm);
        box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 12px;
        width: var(--node-overlay-width);
        flex-direction: column;
        font-size: small;
        transition: box-shadow 0.2s ease-in-out;
    }

    .node-overlay .bar-label {
        display: flex;
        justify-content: space-between;
    }

    .node-overlay div:not(:last-child) .bar-label {
        border-bottom: 1px solid #ddd;
        padding-bottom: var(--ug-spacing-xxs);
        margin-bottom: var(--ug-spacing-xxs);
    }

    .node-overlay .bar-label span {
        font-weight: 600;
    }
</style>
